import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Alert,
  Divider,
  Paper,
  TextField,
  Stack,
  MenuItem,
} from '@mui/material';
import { gql } from '@apollo/client';
import { enqueueSnackbar } from 'notistack';
import { useMutation } from '@pv/common/hooks';

const setVenueOpentableCredentialsMutation = gql`
  mutation SetVenueOpentableCredentials(
    $input: SetVenueOpentableCredentialsInput!
  ) {
    setVenueOpentableCredentials(input: $input) {
      adminVenue {
        id
        opentableRestaurantId
        opentableConnected
      }
      errors {
        message
      }
    }
  }
`;

const removeVenueOpentableCredentialsMutation = gql`
  mutation RemoveVenueOpentableCredentials(
    $input: RemoveVenueOpentableCredentialsInput!
  ) {
    removeVenueOpentableCredentials(input: $input) {
      adminVenue {
        id
        opentableRestaurantId
        opentableConnected
      }
      errors {
        message
      }
    }
  }
`;

const setVenueOpentableCustomInstructionsMutation = gql`
  mutation SetVenueOpentableCustomInstructions(
    $input: SetVenueOpentableCustomInstructionsInput!
  ) {
    setVenueOpentableCustomInstructions(input: $input) {
      adminVenue {
        id
        opentableCustomInstructions
      }
      errors {
        message
      }
    }
  }
`;

const setVenueOpentableSyncTriggerStatusMutation = gql`
  mutation SetVenueOpentableSyncTriggerStatus(
    $input: SetVenueOpentableSyncTriggerStatusInput!
  ) {
    setVenueOpentableSyncTriggerStatus(input: $input) {
      adminVenue {
        id
        opentableSyncTriggerStatus
      }
      errors {
        message
      }
    }
  }
`;

const OPENTABLE_SYNC_TRIGGER_STATUS_OPTIONS = [
  { value: 'lead', label: 'Lead' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'balance_due', label: 'Balance Due' },
];

export const IntegrationsPanel = ({ venue }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [restaurantId, setRestaurantId] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [customInstructions, setCustomInstructions] = useState('');
  const [syncTriggerStatus, setSyncTriggerStatus] = useState('confirmed');

  const [setCredentials, { loading: settingCredentials }] = useMutation(
    setVenueOpentableCredentialsMutation,
    {
      onNoErrorsCompleted: () => {
        enqueueSnackbar('OpenTable integration configured successfully', {
          variant: 'success',
        });
        setPassword(''); // Clear password after successful save
      },
    }
  );

  const [removeCredentials, { loading: removingCredentials }] = useMutation(
    removeVenueOpentableCredentialsMutation,
    {
      onNoErrorsCompleted: () => {
        enqueueSnackbar('OpenTable integration removed successfully', {
          variant: 'success',
        });
        setEmail('');
        setPassword('');
        setRestaurantId('');
      },
    }
  );

  const [saveCustomInstructions, { loading: savingInstructions }] = useMutation(
    setVenueOpentableCustomInstructionsMutation,
    {
      onNoErrorsCompleted: () => {
        enqueueSnackbar('Custom instructions saved successfully', {
          variant: 'success',
        });
      },
    }
  );

  const [saveSyncTriggerStatus, { loading: savingSyncTriggerStatus }] =
    useMutation(setVenueOpentableSyncTriggerStatusMutation, {
      onNoErrorsCompleted: () => {
        enqueueSnackbar('OpenTable sync trigger saved successfully', {
          variant: 'success',
        });
      },
    });

  useEffect(() => {
    if (venue.opentableRestaurantId) {
      setRestaurantId(venue.opentableRestaurantId);
    }
    setCustomInstructions(venue.opentableCustomInstructions || '');
    setSyncTriggerStatus(venue.opentableSyncTriggerStatus || 'confirmed');
  }, [venue]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!email || !password || !restaurantId) {
      enqueueSnackbar('Please fill in all fields', { variant: 'error' });
      return;
    }

    const variables = {
      input: {
        venueId: venue.id,
        email,
        password,
        restaurantId,
      },
    };
    setCredentials({ variables });
  };

  const onRemove = () => {
    if (
      !window.confirm(
        'Are you sure you want to remove the OpenTable integration? This will delete the credentials.'
      )
    ) {
      return;
    }

    const variables = {
      input: {
        venueId: venue.id,
      },
    };
    removeCredentials({ variables });
  };

  const onSaveCustomInstructions = () => {
    saveCustomInstructions({
      variables: {
        input: {
          venueId: venue.id,
          customInstructions: customInstructions || null,
        },
      },
    });
  };

  const onSaveSyncTriggerStatus = () => {
    saveSyncTriggerStatus({
      variables: {
        input: {
          venueId: venue.id,
          triggerStatus: syncTriggerStatus,
        },
      },
    });
  };

  const EditButton = () => {
    const label = isEditing
      ? 'Cancel'
      : venue.opentableConnected
      ? 'Update'
      : 'Add';
    return (
      <Button
        variant="contained"
        color="secondary"
        fullWidth={false}
        onClick={() => setIsEditing(!isEditing)}
      >
        {label}
      </Button>
    );
  };

  return (
    <Box p={3}>
      <Typography variant="h5" component="h2" gutterBottom>
        Integrations
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          paddingBottom={2}
        >
          <Typography variant="h6" component="h3">
            OpenTable Integration
          </Typography>

          <EditButton />
        </Stack>
        {venue.opentableConnected && (
          <Alert severity="success" sx={{ mb: 2 }}>
            OpenTable integration is currently active
          </Alert>
        )}

        {!venue.opentableConnected && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No OpenTable integration configured
          </Alert>
        )}

        {isEditing && (
          <form onSubmit={onSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="OpenTable Email"
                  value={email || ''}
                  autoComplete="off"
                  onChange={(e) => setEmail(e.target.value)}
                  name="opentable-email"
                  helperText="The email address used to log in to OpenTable (stored encrypted)"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  type="password"
                  label="OpenTable Password"
                  value={password || ''}
                  autoComplete="off"
                  onChange={(e) => setPassword(e.target.value)}
                  name="opentable-password"
                  helperText="The password for the OpenTable account (stored encrypted)"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Restaurant ID"
                  value={restaurantId || ''}
                  onChange={(e) => setRestaurantId(e.target.value)}
                  name="opentable-restaurant-id"
                  helperText="The OpenTable restaurant identifier"
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={2} mt={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={settingCredentials}
                  >
                    {venue.opentableConnected
                      ? 'Update OpenTable Integration'
                      : 'Add OpenTable Integration'}
                  </Button>

                  {venue.opentableConnected && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={onRemove}
                      disabled={removingCredentials}
                    >
                      Remove OpenTable Integration
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          OpenTable Sync Trigger
        </Typography>
        <TextField
          fullWidth
          select
          label="Trigger Status"
          value={syncTriggerStatus}
          onChange={(e) => setSyncTriggerStatus(e.target.value)}
          helperText="OpenTable sync starts when an event reaches this status."
        >
          {OPENTABLE_SYNC_TRIGGER_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Box display="flex" mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={onSaveSyncTriggerStatus}
            disabled={savingSyncTriggerStatus}
          >
            {savingSyncTriggerStatus ? 'Saving...' : 'Save Trigger'}
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Custom Instructions for OpenTable Syncs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These instructions will be shown in the OpenTable Sync Dashboard when
          processing syncs for this venue.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          label="Custom Instructions"
          value={customInstructions}
          onChange={(e) => setCustomInstructions(e.target.value)}
          placeholder="e.g., Always set group size to 1 for this venue"
        />
        <Box display="flex" mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={onSaveCustomInstructions}
            disabled={savingInstructions}
          >
            {savingInstructions ? 'Saving...' : 'Save Instructions'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
